const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://plateforme.fhcgroupesarl.com/api';

// Récupérer le token depuis localStorage
const getToken = () => {
  return localStorage.getItem('fhc_token');
};

// Headers par défaut https://plateforme.fhcgroupesarl.com/api/
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  'Authorization': `Bearer ${getToken()}`,
});

// Gestion des erreurs AMÉLIORÉE
const handleResponse = async (response, skipRedirect = false) => {
  console.log(`📡 API Response: ${response.status} ${response.statusText}`);
  
  if (!response.ok) {
    if (response.status === 401) {
      console.error('🔴 401 Unauthorized - Token invalide ou expiré');
      localStorage.removeItem('fhc_token');
      localStorage.removeItem('fhc_user');
      
      if (!skipRedirect && !window.location.pathname.includes('/auth')) {
        window.location.href = '/auth';
      }
      throw new Error('Session expirée - Veuillez vous reconnecter');
    }
    
    if (response.status === 422) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { message: 'Erreur de validation' };
      }
      
      console.error('🔴 422 Validation Error:', errorData);
      
      let errorMessage = errorData.message || 'Erreur de validation';
      if (errorData.errors) {
        const errorDetails = Object.entries(errorData.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('\\n');
        errorMessage += '\\n' + errorDetails;
      }
      
      const error = new Error(errorMessage);
      error.status = 422;
      error.data = errorData;
      throw error;
    }
    
    if (response.status === 404) {
      console.error("🔴 404 Not Found - La route n\\'existe pas");
      throw new Error('Route API non trouvée (404)');
    }
    
    if (response.status >= 500) {
      console.error('🔴 500 Server Error');
      throw new Error('Erreur serveur - Veuillez réessayer plus tard');
    }
    
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      errorData = { message: `Erreur HTTP ${response.status}` };
    }
    
    console.error(`🔴 HTTP ${response.status}:`, errorData);
    throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
  }
  
  try {
    const data = await response.json();
    console.log('✅ API Success:', data);
    return data;
  } catch (e) {
    console.warn('⚠️ Réponse non-JSON:', response.statusText);
    return { success: true };
  }
};

// ========================================
// AUTHENTIFICATION
// ========================================
export const authAPI = {
  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return handleResponse(response);
  },

  logout: async () => {
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  me: async () => {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: getHeaders(),
    });
    return handleResponse(response, true);
  },
};

// ========================================
// TÂCHES
// ========================================
export const tasksAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/tasks?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data) => {
    console.log('📤 tasksAPI.create - Payload:', data);
    const response = await fetch(`${API_BASE_URL}/tasks`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/tasks/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/tasks/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  toggle: async (id) => {
    const response = await fetch(`${API_BASE_URL}/tasks/${id}/toggle`, {
      method: 'PATCH',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  assign: async (id, employeeId) => {
    const response = await fetch(`${API_BASE_URL}/tasks/${id}/assign`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ employee_id: employeeId }),
    });
    return handleResponse(response);
  },

  uploadProof: async (id, file) => {
    const formData = new FormData();
    formData.append('document_preuve', file);
    const response = await fetch(`${API_BASE_URL}/tasks/${id}/upload-proof`, {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${getToken()}` },
      body: formData,
    });
    return handleResponse(response);
  },

  downloadProof: async (id) => {
    const response = await fetch(`${API_BASE_URL}/tasks/${id}/download-proof`, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    
    if (!response.ok) {
      throw new Error('Impossible de télécharger le document');
    }
    
    return response.blob();
  },

  getProofUrl: (id) => {
    return `${API_BASE_URL}/tasks/${id}/download-proof`;
  },
};

// ========================================
// PROJETS
// ========================================
export const projectsAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/projects?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/projects/${id}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/projects`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
};

// ========================================
// PAIE
// ========================================
export const paieAPI = {
  bulletins: {
    getAll: async (filters = {}) => {
      // Filtrer les valeurs vides pour ne pas les envoyer au backend
      const cleanFilters = Object.entries(filters)
        .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
      
      const params = new URLSearchParams(cleanFilters);
      const response = await fetch(`${API_BASE_URL}/bulletins-paie?${params}`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    getById: async (id) => {
      const response = await fetch(`${API_BASE_URL}/bulletins-paie/${id}`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    getByMois: async (mois) => {
      const response = await fetch(`${API_BASE_URL}/bulletins-paie/mois/${mois}`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    calculer: async (data) => {
      const response = await fetch(`${API_BASE_URL}/bulletins-paie/calculer`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    create: async (data) => {
      const response = await fetch(`${API_BASE_URL}/bulletins-paie`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    update: async (id, data) => {
      const response = await fetch(`${API_BASE_URL}/bulletins-paie/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    delete: async (id) => {
      const response = await fetch(`${API_BASE_URL}/bulletins-paie/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    valider: async (id) => {
      const response = await fetch(`${API_BASE_URL}/bulletins-paie/${id}/valider`, {
        method: 'POST',
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    payer: async (id, data) => {
      const response = await fetch(`${API_BASE_URL}/bulletins-paie/${id}/payer`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    genererMasse: async (data) => {
      const response = await fetch(`${API_BASE_URL}/bulletins-paie/generer-masse`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    statsMois: async (mois) => {
      const response = await fetch(`${API_BASE_URL}/bulletins-paie/stats/${mois}`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    synthese: async () => {
      const response = await fetch(`${API_BASE_URL}/bulletins-paie/synthese`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
  },

  declarations: {
    getAll: async (filters = {}) => {
      const params = new URLSearchParams(filters);
      const response = await fetch(`${API_BASE_URL}/declarations-sociales?${params}`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    generer: async (mois) => {
      const response = await fetch(`${API_BASE_URL}/declarations-sociales/generer`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ mois }),
      });
      return handleResponse(response);
    },
  },
};

// ========================================
// FINANCE
// ========================================
export const financeAPI = {
  transactions: {
    getAll: async (filters = {}) => {
      const params = new URLSearchParams(filters);
      const response = await fetch(`${API_BASE_URL}/finances/transactions?${params}`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    create: async (data) => {
      const response = await fetch(`${API_BASE_URL}/finances/transactions`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },
  },

  synthese: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/finances/synthese?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  caParFiliale: async (annee) => {
    const response = await fetch(`${API_BASE_URL}/finances/ca-par-filiale?annee=${annee}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  resultatMensuel: async (annee) => {
    const response = await fetch(`${API_BASE_URL}/finances/resultat-mensuel?annee=${annee}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  tresorerie: async () => {
    const response = await fetch(`${API_BASE_URL}/finances/tresorerie`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// LOCATION (FHC Location)
// ========================================
export const locationAPI = {
  biens: {
    getAll: async (filters = {}) => {
      const params = new URLSearchParams(filters);
      const response = await fetch(`${API_BASE_URL}/biens?${params}`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
  },

  loyers: {
    getAll: async (filters = {}) => {
      const params = new URLSearchParams(filters);
      const response = await fetch(`${API_BASE_URL}/loyers?${params}`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    marquerPaye: async (id, data) => {
      const response = await fetch(`${API_BASE_URL}/loyers/${id}/payer`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },
  },
};

// ========================================
// RÉUNIONS & COLLABORATION
// ========================================
export const collaborationAPI = {
  reunions: {
    getAll: async (filters = {}) => {
      const params = new URLSearchParams(filters);
      const response = await fetch(`${API_BASE_URL}/reunions?${params}`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    futures: async () => {
      const response = await fetch(`${API_BASE_URL}/reunions/futures`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    create: async (data) => {
      const response = await fetch(`${API_BASE_URL}/reunions`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },
  },

  pv: {
    getAll: async () => {
      const response = await fetch(`${API_BASE_URL}/pv`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    create: async (data) => {
      const response = await fetch(`${API_BASE_URL}/pv`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    update: async (id, data) => {
      const response = await fetch(`${API_BASE_URL}/pv/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    valider: async (id) => {
      const response = await fetch(`${API_BASE_URL}/pv/${id}/valider`, {
        method: 'POST',
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    getActions: async (pvId) => {
      const response = await fetch(`${API_BASE_URL}/pv/${pvId}/actions`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    ajouterAction: async (pvId, data) => {
      const response = await fetch(`${API_BASE_URL}/pv/${pvId}/actions`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    mettreAJourAction: async (pvId, actionId, data) => {
      const response = await fetch(`${API_BASE_URL}/pv/${pvId}/actions/${actionId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    terminerAction: async (pvId, actionId) => {
      const response = await fetch(`${API_BASE_URL}/pv/${pvId}/actions/${actionId}/terminer`, {
        method: 'PATCH',
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    actionsEnCours: async () => {
      const response = await fetch(`${API_BASE_URL}/actions/en-cours`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    ajouterDecision: async (pvId, data) => {
      const response = await fetch(`${API_BASE_URL}/pv/${pvId}/decisions`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    mettreAJourDecision: async (pvId, decisionId, data) => {
      const response = await fetch(`${API_BASE_URL}/pv/${pvId}/decisions/${decisionId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    supprimerDecision: async (pvId, decisionId) => {
      const response = await fetch(`${API_BASE_URL}/pv/${pvId}/decisions/${decisionId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    statistiques: async () => {
      const response = await fetch(`${API_BASE_URL}/pv/stats/global`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
  },

  rdv: {
    getAll: async (filters = {}) => {
      const params = new URLSearchParams(filters);
      const response = await fetch(`${API_BASE_URL}/rdv?${params}`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    futurs: async () => {
      const response = await fetch(`${API_BASE_URL}/rdv/futurs`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },

    create: async (data) => {
      const response = await fetch(`${API_BASE_URL}/rdv`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },
  },
};

// ========================================
// POINTAGE
// ========================================
export const pointageAPI = {
  genererCode: async (date) => {
    const response = await fetch(`${API_BASE_URL}/pointage/generer`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ date }),
    });
    return handleResponse(response);
  },

  codeJour: async () => {
    const response = await fetch(`${API_BASE_URL}/pointage/code-jour`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  verifier: async (code) => {
    const response = await fetch(`${API_BASE_URL}/pointage/verifier`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ code }),
    });
    return handleResponse(response);
  },

  marquerFin: async (date = null) => {
    const response = await fetch(`${API_BASE_URL}/pointage/marquer-fin`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ date }),
    });
    return handleResponse(response);
  },
};

// ========================================
// DASHBOARDS
// ========================================
export const dashboardAPI = {
  dg: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString 
      ? `${API_BASE_URL}/dashboard/dg?${queryString}`
      : `${API_BASE_URL}/dashboard/dg`;
    const response = await fetch(url, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  comptabilite: async () => {
    const response = await fetch(`${API_BASE_URL}/dashboard/comptabilite`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  rh: async () => {
    const response = await fetch(`${API_BASE_URL}/dashboard/rh`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  location: async () => {
    const response = await fetch(`${API_BASE_URL}/dashboard/location`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  marketing: async () => {
    const response = await fetch(`${API_BASE_URL}/dashboard/marketing`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  projets: async () => {
    const response = await fetch(`${API_BASE_URL}/dashboard/projets`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  technologie: async () => {
    const response = await fetch(`${API_BASE_URL}/dashboard/technologie`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// RÉFÉRENTIELS
// ========================================
export const referentielsAPI = {
  filiales: async () => {
    const response = await fetch(`${API_BASE_URL}/filiales`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  departements: async () => {
    const response = await fetch(`${API_BASE_URL}/departements`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// EMPLOYEES CRUD
// ========================================
export const employeesCRUD = {
  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/employees`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/employees/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/employees/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// CRÉATION DE RESSOURCES COMPLÈTES
// ========================================
export const completeResourcesAPI = {
  createEmployeeWithUser: async (data) => {
    const response = await fetch(`${API_BASE_URL}/resources/employee-with-user`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  createDepartement: async (data) => {
    const response = await fetch(`${API_BASE_URL}/resources/departement`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  createProject: async (data) => {
    const response = await fetch(`${API_BASE_URL}/resources/project`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  createFiliale: async (data) => {
    const response = await fetch(`${API_BASE_URL}/resources/filiale`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
};

// ========================================
// PROJETS CRUD complet
// ========================================
export const projectsCRUD = {
  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/projects/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/projects/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// TRANSACTIONS FINANCIÈRES CRUD
// ========================================
export const transactionsAPI = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/finances/transactions`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/finances/transactions`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/finances/transactions/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/finances/transactions/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// OBJECTIFS CRUD
// ========================================
export const objectifsAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/objectifs?${params}`, { headers: getHeaders() });
    return handleResponse(response);
  },
  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/objectifs`, {
      method: 'POST', headers: getHeaders(), body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/objectifs/${id}`, {
      method: 'PUT', headers: getHeaders(), body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  updateProgression: async (id, progression, commentaire = '') => {
    const response = await fetch(`${API_BASE_URL}/objectifs/${id}/progression`, {
      method: 'PATCH', headers: getHeaders(), body: JSON.stringify({ progression, commentaire }),
    });
    return handleResponse(response);
  },
  updateStatut: async (id, statut, commentaire = '') => {
    const response = await fetch(`${API_BASE_URL}/objectifs/${id}/statut`, {
      method: 'PATCH', headers: getHeaders(), body: JSON.stringify({ statut, commentaire }),
    });
    return handleResponse(response);
  },
  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/objectifs/${id}`, {
      method: 'DELETE', headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// RÉUNIONS CRUD
// ========================================
export const reunionsAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/reunions?${params}`, { headers: getHeaders() });
    return handleResponse(response);
  },
  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/reunions`, {
      method: 'POST', headers: getHeaders(), body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/reunions/${id}`, {
      method: 'PUT', headers: getHeaders(), body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/reunions/${id}`, {
      method: 'DELETE', headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// ALERTES & NOTIFICATIONS
// ========================================
export const alertesAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/alertes?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  nonLues: async () => {
    const response = await fetch(`${API_BASE_URL}/alertes/non-lues`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  statistiques: async () => {
    const response = await fetch(`${API_BASE_URL}/alertes/statistiques`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  generer: async () => {
    const response = await fetch(`${API_BASE_URL}/alertes/generer`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  marquerLue: async (id) => {
    const response = await fetch(`${API_BASE_URL}/alertes/${id}/lire`, {
      method: 'PATCH',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  resoudre: async (id) => {
    const response = await fetch(`${API_BASE_URL}/alertes/${id}/resoudre`, {
      method: 'PATCH',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/alertes`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/alertes/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// FILIALES CRUD
// ========================================
export const filialesAPI = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/filiales`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/filiales`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/filiales/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/filiales/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// DÉPARTEMENTS CRUD
// ========================================
export const departementsAPI = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/departements`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/departements`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/departements/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/departements/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// FILIALES CRUD (alias pour compatibilité)
// ========================================
export const filialesCRUD = filialesAPI;
export const departementsCRUD = departementsAPI;

// ========================================
// USERS
// ========================================
export const usersAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/users?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// EMPLOYÉS
// ========================================
export const employeesAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/employees?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/employees/${id}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getByFiliale: async (filialeId) => {
    const response = await fetch(`${API_BASE_URL}/employees/filiale/${filialeId}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getDGList: async (filialeId = null) => {
    const params = filialeId ? `?filiale_id=${filialeId}` : '';
    const response = await fetch(`${API_BASE_URL}/employees-dg/list${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  historiquePointages: async (id) => {
    const response = await fetch(`${API_BASE_URL}/employees/${id}/historique-pointages`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  projects: async (id) => {
    const response = await fetch(`${API_BASE_URL}/employees/${id}/projects`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// ÉTATS FINANCIERS
// ========================================
export const etatsFinanciersAPI = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/etats-financiers`, { headers: getHeaders() });
    return handleResponse(response);
  },
  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/etats-financiers/${id}`, { headers: getHeaders() });
    return handleResponse(response);
  },
  getLatest: async () => {
    const response = await fetch(`${API_BASE_URL}/etats-financiers-latest`, { headers: getHeaders() });
    return handleResponse(response);
  },
  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/etats-financiers`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(data) });
    return handleResponse(response);
  },
  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/etats-financiers/${id}`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify(data) });
    return handleResponse(response);
  },
  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/etats-financiers/${id}`, { method: 'DELETE', headers: getHeaders() });
    return handleResponse(response);
  },
};

export const caisseAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/operations-caisse?${params}`, { headers: getHeaders() });
    return handleResponse(response);
  },
  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/operations-caisse`, {
      method: 'POST', headers: getHeaders(), body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/operations-caisse/${id}`, {
      method: 'PUT', headers: getHeaders(), body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/operations-caisse/${id}`, {
      method: 'DELETE', headers: getHeaders(),
    });
    return handleResponse(response);
  },
  genererNumero: async (type = 'enc') => {
    const response = await fetch(`${API_BASE_URL}/operations-caisse-numero?type=${type}`, { headers: getHeaders() });
    return handleResponse(response);
  },
  getSoldeInitial: async (params = {}) => {
    const queryParams = new URLSearchParams(params);
    const response = await fetch(`${API_BASE_URL}/solde-initial-caisse?${queryParams}`, { headers: getHeaders() });
    return handleResponse(response);
  },
  saveSoldeInitial: async (data) => {
    const response = await fetch(`${API_BASE_URL}/solde-initial-caisse`, {
      method: 'POST', headers: getHeaders(), body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  listSoldesInitiaux: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/soldes-initiaux-caisse?${params}`, { headers: getHeaders() });
    return handleResponse(response);
  },
  exportPDF: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/operations-caisse-export-pdf?${params}`, { 
      headers: getHeaders(),
      method: 'GET'
    });
    
    if (!response.ok) {
      throw new Error('Erreur lors de l\'exportation PDF');
    }
    
    // Récupérer le blob PDF
    const blob = await response.blob();
    
    // Créer un lien de téléchargement
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Générer un nom de fichier avec la date actuelle
    const dateStr = new Date().toISOString().split('T')[0];
    link.download = `journal-caisse-${dateStr}.pdf`;
    
    // Déclencher le téléchargement
    document.body.appendChild(link);
    link.click();
    
    // Nettoyer
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true, message: 'PDF téléchargé avec succès' };
  },

  // Sessions de caisse
  ouvrirCaisse: async (data) => {
    const response = await fetch(`${API_BASE_URL}/caisse/ouvrir`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  fermerCaisse: async (data = {}) => {
    const response = await fetch(`${API_BASE_URL}/caisse/fermer`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  getSessionCaisse: async () => {
    const response = await fetch(`${API_BASE_URL}/caisse/session`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  historiqueSessionsCaisse: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/caisse/historique?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};


// ========================================
// CRM - PROSPECTS
// ========================================
export const prospectsAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/prospects?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/prospects/${id}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/prospects`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/prospects/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/prospects/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  changerTemperature: async (id, temperature) => {
    const response = await fetch(`${API_BASE_URL}/prospects/${id}/temperature`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ temperature }),
    });
    return handleResponse(response);
  },

  convertirEnProjet: async (id, data = {}) => {
    const response = await fetch(`${API_BASE_URL}/prospects/${id}/convertir`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
};

// ========================================
// CRM - DEVIS
// ========================================
export const devisAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/devis?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/devis/${id}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/devis`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/devis/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/devis/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  envoyer: async (id) => {
    const response = await fetch(`${API_BASE_URL}/devis/${id}/envoyer`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  accepter: async (id) => {
    const response = await fetch(`${API_BASE_URL}/devis/${id}/accepter`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  refuser: async (id) => {
    const response = await fetch(`${API_BASE_URL}/devis/${id}/refuser`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// CRM - RELANCES
// ========================================
export const relancesAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/relances?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/relances/${id}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/relances`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/relances/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/relances/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  marquerEffectue: async (id, data = {}) => {
    const response = await fetch(`${API_BASE_URL}/relances/${id}/effectuer`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  reporter: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/relances/${id}/reporter`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
};

// ========================================
// CRM - ACTIVITÉS COMMERCIALES
// ========================================
export const activitesCommercialesAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/activites-commerciales?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/activites-commerciales/${id}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getByProspect: async (prospectId) => {
    const response = await fetch(`${API_BASE_URL}/activites-commerciales/prospect/${prospectId}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/activites-commerciales`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/activites-commerciales/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/activites-commerciales/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// CRM - APPRENANTS
// ========================================
export const apprenantsAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/apprenants?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/apprenants/${id}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/apprenants`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/apprenants/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/apprenants/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// DEMANDES DE PERMISSION
// ========================================
export const demandesPermissionAPI = {
  // Obtenir toutes les demandes (DG)
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/demandes-permission?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Obtenir MES demandes (employé connecté)
  mesDemandes: async () => {
    const response = await fetch(`${API_BASE_URL}/demandes-permission/mes-demandes`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Créer une nouvelle demande
  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/demandes-permission`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  // Approuver une demande (DG)
  approuver: async (id, commentaire = '') => {
    const response = await fetch(`${API_BASE_URL}/demandes-permission/${id}/approuver`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ commentaire }),
    });
    return handleResponse(response);
  },

  // Refuser une demande (DG)
  refuser: async (id, commentaire) => {
    const response = await fetch(`${API_BASE_URL}/demandes-permission/${id}/refuser`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ commentaire }),
    });
    return handleResponse(response);
  },

  // Supprimer une demande (employé, si en attente)
  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/demandes-permission/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Statistiques
  statistiques: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/demandes-permission/statistiques?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// PERFORMANCE
// ========================================
export const performanceAPI = {
  // Calculer et mettre à jour le score de performance d'un employé
  calculate: async (employeeId, dateDebut = null, dateFin = null) => {
    const params = new URLSearchParams();
    if (dateDebut) params.append('date_debut', dateDebut);
    if (dateFin) params.append('date_fin', dateFin);
    
    const response = await fetch(`${API_BASE_URL}/performance/employee/${employeeId}/calculer?${params}`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Obtenir le score de performance d'un employé
  get: async (employeeId, dateDebut = null, dateFin = null) => {
    const params = new URLSearchParams();
    if (dateDebut) params.append('date_debut', dateDebut);
    if (dateFin) params.append('date_fin', dateFin);
    
    const response = await fetch(`${API_BASE_URL}/performance/employee/${employeeId}?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Mettre à jour les scores manuels (travail en équipe et respect des règles)
  updateManualScores: async (employeeId, scoreTravailEquipe, scoreRespectRegles) => {
    const response = await fetch(`${API_BASE_URL}/performance/employee/${employeeId}/scores-manuels`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({
        score_travail_equipe: scoreTravailEquipe,
        score_respect_regles: scoreRespectRegles,
      }),
    });
    return handleResponse(response);
  },

  // Obtenir les performances de tous les employés
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/performance/tous?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Recalculer les performances de tous les employés
  recalculateAll: async (dateDebut = null, dateFin = null) => {
    const params = new URLSearchParams();
    if (dateDebut) params.append('date_debut', dateDebut);
    if (dateFin) params.append('date_fin', dateFin);
    
    const response = await fetch(`${API_BASE_URL}/performance/recalculer-tous?${params}`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Obtenir les statistiques globales
  getStatistics: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/performance/statistiques?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

// ========================================
// PROCES-VERBAUX
// ========================================
export const procesVerbauxAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE_URL}/pv?${params}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/pv/${id}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/pv`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/pv/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/pv/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  valider: async (id) => {
    const response = await fetch(`${API_BASE_URL}/pv/${id}/valider`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  parReunion: async (reunionId) => {
    const response = await fetch(`${API_BASE_URL}/pv/reunion/${reunionId}`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  statistiques: async () => {
    const response = await fetch(`${API_BASE_URL}/pv/stats/global`, {
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

const api = {
  auth: authAPI,
  users: usersAPI,
  employees: employeesAPI,
  employeesCRUD,
  completeResources: completeResourcesAPI,
  tasks: tasksAPI,
  projects: projectsAPI,
  projectsCRUD,
  objectifs: objectifsAPI,
  reunions: reunionsAPI,
  paie: paieAPI,
  finance: financeAPI,
  transactions: transactionsAPI,
  location: locationAPI,
  collaboration: collaborationAPI,
  pointage: pointageAPI,
  dashboard: dashboardAPI,
  alertes: alertesAPI,
  filiales: filialesAPI,
  departements: departementsAPI,
  filialesCRUD,
  departementsCRUD,
  codeJour: pointageAPI.codeJour,
  genererCode: pointageAPI.genererCode,
  verifierCode: pointageAPI.verifier,
  caisse: caisseAPI,
  etatsFinanciers: etatsFinanciersAPI,
  // CRM
  prospects: prospectsAPI,
  devis: devisAPI,
  relances: relancesAPI,
  activitesCommerciales: activitesCommercialesAPI,
  apprenants: apprenantsAPI,
  // Performance
  performance: performanceAPI,
  // Demandes de Permission
  demandesPermission: demandesPermissionAPI,
  // Procès-Verbaux
  procesVerbaux: procesVerbauxAPI,
};

export default api;
